import type { Components, Theme } from '@mui/material/styles';

/** Shared layout defaults; domain tables still choose their readable minimum width. */
export const layoutOverrides = (theme: Theme): Components<Theme> => {
  const phone = theme.breakpoints.down('sm');
  const panelPadding = { padding: theme.spacing(3), [phone]: { padding: theme.spacing(2) } };

  return {
    // CSS gap keeps wrapped rows aligned; margin-based spacing indents the next line.
    MuiStack: { defaultProps: { useFlexGap: true } },
    MuiCard: { styleOverrides: { root: { minWidth: 0 } } },
    MuiCardActionArea: {
      styleOverrides: {
        root: {
          '&:focus-visible, &.Mui-focusVisible': {
            outline: `2px solid ${theme.palette.readable.link}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: { ...panelPadding, gap: theme.spacing(2), alignItems: 'flex-start', flexWrap: 'wrap' },
        content: { minWidth: 0, flex: '1 1 12rem', overflowWrap: 'anywhere' },
        avatar: { marginRight: 0 },
        action: { margin: 0 },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          ...panelPadding,
          minWidth: 0,
          // A header already supplies the upper gutter for its following content.
          '.MuiCardHeader-root + &': { paddingTop: 0 },
          '&:last-child': { paddingBottom: theme.spacing(3) },
          [phone]: { padding: theme.spacing(2), '&:last-child': { paddingBottom: theme.spacing(2) } },
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 3),
          flexWrap: 'wrap',
          gap: theme.spacing(1),
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          [phone]: { padding: theme.spacing(2) },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          minWidth: 0,
          [phone]: { margin: 8, width: 'calc(100% - 16px)', maxHeight: 'calc(100dvh - 16px)' },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: { overflowWrap: 'anywhere', [phone]: { padding: theme.spacing(2) } },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          minWidth: 0,
          overflowWrap: 'anywhere',
          [phone]: { paddingLeft: theme.spacing(2), paddingRight: theme.spacing(2) },
          // Outlined inputs need space above their floating label, even after a title.
          '.MuiDialogTitle-root + &': { paddingTop: theme.spacing(1) },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: theme.spacing(2, 3),
          flexWrap: 'wrap',
          gap: theme.spacing(1),
          '& > :not(style) ~ :not(style)': { marginLeft: 0 },
          [phone]: {
            padding: theme.spacing(2),
            flexDirection: 'column',
            alignItems: 'stretch',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          minWidth: 0,
          maxWidth: '100%',
          overflowX: 'auto',
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.readable.link}`,
            outlineOffset: -2,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          // Freeform page text may break anywhere; a table must retain word widths.
          overflowWrap: 'normal',
          wordBreak: 'normal',
          verticalAlign: 'top',
        },
        head: { whiteSpace: 'nowrap', fontWeight: 600 },
        alignRight: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
      },
    },
    MuiStepLabel: { styleOverrides: { root: { overflowWrap: 'normal', wordBreak: 'normal' } } },
    MuiTabs: { styleOverrides: { root: { minWidth: 0 } } },
    MuiBreadcrumbs: {
      styleOverrides: {
        root: { minWidth: 0 },
        li: { minWidth: 0, overflowWrap: 'anywhere' },
      },
    },
  };
};
